import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { PhoneTicket, OrdersMini, QrScanDemo } from "@/components/arranger/TicketDemo";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

const EASE = [0.2, 0.8, 0.2, 1];

/* One ticket – KA4821 – followed through all three beats. Durations match one full loop of each demo. */
export const STORY = [
  { id: "reservasjon", who: "Publikum", short: "Reservasjon", title: "Publikum reserverer", text: "Vel tal billettar på nettsida, får kode og Vipps-instruks til ditt nummer. Betaler dei ikkje innan fristen, fell plassane bort av seg sjølv.", caption: "Slik ser billetten ut på mobilen til publikum", duration: 10700, Demo: PhoneTicket },
  { id: "betaling", who: "Du", short: "Betaling", title: "Du registrerer betalinga", text: "Pengane kjem i Vipps-appen din. Eitt trykk på «Motteke», så vert billetten gyldig og QR-koden sendt til publikum.", caption: "Arrangørsida di – oppdaterer seg sjølv", duration: 8550, Demo: OrdersMini },
  { id: "dora", who: "I døra", short: "I døra", title: "QR-koden vert skanna i døra", text: "Opne arrangørsida på mobilen, hald kameraet mot billetten. Grønt svar med lyd og vibrasjon – òg i mørkret. Brukte billettar slepp ikkje inn to gonger.", caption: "Dørmodus på mobilen – fungerer i låglys", duration: 7800, Demo: QrScanDemo },
];

/** Thin sol line that fills over `duration`; isolated so the 60 fps ticks never re-render the demos. */
const StepProgress = ({ duration, playing, onDone, className }) => {
  const [p, setP] = useState(0);
  const elapsed = useRef(0);
  useEffect(() => {
    if (!playing) return undefined;
    let raf; let last = performance.now();
    const tick = (t) => {
      elapsed.current += t - last; last = t;
      const next = Math.min(1, elapsed.current / duration);
      setP(next);
      if (next >= 1) { onDone(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, duration, onDone]);
  return <span className={cn("block h-full origin-left bg-sol", className)} style={{ transform: `scaleX(${p})` }} aria-hidden="true" />;
};

const WhoPill = ({ children, active }) => (
  <span className={cn("t-mono-sm rounded-full border px-2 py-0.5 transition-colors", active ? "border-sol/60 text-sol" : "border-paper/15 text-paper/45")}>{children}</span>
);

/**
 * «Slik verkar billettane» – a guided, self-advancing walkthrough. Left: the three beats as tabs (vertical ≥ lg,
 * horizontal below). Right: a single stage that cross-fades the live product demo for the active beat.
 * Auto-advance starts when the section scrolls into view, pauses on hover/focus or when the tab is hidden,
 * and is off entirely for reduced motion.
 */
export const TicketStory = ({ className }) => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(true);
  const [userPaused, setUserPaused] = useState(false);
  const rootRef = useRef(null);
  const desktop = useMediaQuery("(min-width: 1024px)", true);
  const step = STORY[active];
  const playing = !reduce && !userPaused && !hover && inView && visible;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const go = (i) => setActive(((i % STORY.length) + STORY.length) % STORY.length);
  const next = useCallback(() => setActive((a) => (a + 1) % STORY.length), []);
  const onKey = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); go(active + 1); }
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); go(active - 1); }
  };

  return (
    <div ref={rootRef} className={cn("grid gap-6 lg:grid-cols-12 lg:items-start lg:gap-10", className)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocusCapture={() => setHover(true)} onBlurCapture={() => setHover(false)} data-testid="ticket-story" data-active={step.id} data-playing={playing ? "true" : "false"}>
      {/* Beats – vertical rail on desktop */}
      {desktop && <div className="flex flex-col lg:col-span-5 lg:self-stretch">
        <ol role="tablist" aria-label="Dei tre stega" aria-orientation="vertical" className="border-b border-paper/12" onKeyDown={onKey} data-testid="ticket-story-tabs">
          {STORY.map((s, i) => {
            const on = i === active;
            return (
              <li key={s.id} className="relative border-t border-paper/12">
                {on && <span className="absolute inset-x-0 -top-px h-px overflow-hidden" data-testid="ticket-story-progress"><StepProgress key={s.id} duration={s.duration} playing={playing} onDone={next} /></span>}
                <button type="button" role="tab" id={`ticket-story-tab-${s.id}`} aria-selected={on} aria-controls="ticket-story-stage" tabIndex={on ? 0 : -1} onClick={() => go(i)} className={cn("group flex w-full gap-6 py-6 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol/70 focus-visible:ring-offset-4 focus-visible:ring-offset-ink", !on && "hover:[&_h3]:text-paper")} data-testid={`ticket-story-tab-${s.id}`}>
                  <span className={cn("mt-1 font-display text-[15px] font-semibold tabular tracking-[-0.02em] transition-colors", on ? "text-sol" : "text-paper/35")}>0{i + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-3">
                      <h3 className={cn("t-h3 transition-colors", on ? "text-paper" : "text-paper/55")}>{s.title}</h3>
                      <WhoPill active={on}>{s.who}</WhoPill>
                    </span>
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.span key="text" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} className="block overflow-hidden">
                          <span className="block max-w-md pt-3 text-[15px] leading-relaxed text-paper/70">{s.text}</span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <div className="mt-auto flex items-center justify-between gap-4 pt-8 text-[13px] text-paper/55">
          <span className="flex items-center gap-2"><span className="live-dot" aria-hidden="true" />Same billett heile vegen · <span className="tabular text-paper/80">KA4821</span></span>
          {!reduce && (
            <button type="button" onClick={() => setUserPaused((p) => !p)} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-paper/15 px-3 text-[12px] text-paper/70 transition-colors hover:border-paper/40 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol/70" aria-pressed={userPaused} data-testid="ticket-story-toggle">
              {userPaused ? <><Play className="h-3 w-3" /> Spel av</> : <><Pause className="h-3 w-3" /> Pause</>}
            </button>
          )}
        </div>
      </div>}

      {/* Beats – horizontal tabs on mobile */}
      {!desktop && <div>
        <div role="tablist" aria-label="Dei tre stega" className="grid grid-cols-3 gap-2" onKeyDown={onKey} data-testid="ticket-story-tabs-mobile">
          {STORY.map((s, i) => {
            const on = i === active;
            return (
              <button key={s.id} type="button" role="tab" aria-selected={on} aria-controls="ticket-story-stage" tabIndex={on ? 0 : -1} onClick={() => go(i)} className={cn("relative min-w-0 overflow-hidden rounded-[12px] border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol/70", on ? "border-paper/30 bg-paper/[0.06]" : "border-paper/12 hover:border-paper/25")} data-testid={`ticket-story-tab-mobile-${s.id}`}>
                <span className={cn("t-mono-sm block truncate", on ? "text-sol" : "text-paper/45")}>0{i + 1} · {s.who}</span>
                <span className={cn("mt-1 block truncate text-[13.5px] font-medium", on ? "text-paper" : "text-paper/65")}>{s.short}</span>
                {on && <span className="absolute inset-x-0 bottom-0 h-[2px] bg-paper/15"><StepProgress key={s.id} duration={s.duration} playing={playing} onDone={next} /></span>}
              </button>
            );
          })}
        </div>
      </div>}

      {/* Stage */}
      <div className="lg:col-span-7">
        <div id="ticket-story-stage" role="tabpanel" aria-labelledby={`ticket-story-tab-${step.id}`} className="relative flex min-h-[600px] items-center justify-center overflow-hidden rounded-[28px] bg-paper/[0.035] px-3 pb-14 pt-16 ring-1 ring-paper/10 sm:px-10 lg:min-h-[680px]" data-testid="ticket-story-stage">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_55%_at_50%_0%,hsl(var(--sol)/0.13),transparent_70%)]" aria-hidden="true" />
          <div className="absolute inset-x-5 top-5 flex items-center justify-between sm:inset-x-6 sm:top-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-ink/70 px-3 py-1 text-[12px] text-paper/80 ring-1 ring-paper/10 backdrop-blur" data-testid="ticket-story-who"><span className="h-1.5 w-1.5 rounded-full bg-sol" aria-hidden="true" />{step.who}</span>
            <span className="t-mono-sm text-paper/45" data-testid="ticket-story-counter">Steg {active + 1} / {STORY.length}</span>
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={step.id} initial={{ opacity: 0, y: 16, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.99 }} transition={{ duration: 0.45, ease: EASE }} className={cn("relative w-full", active === 0 ? "max-w-[300px]" : active === 1 ? "max-w-[460px]" : "max-w-[440px]")}>
              <step.Demo />
            </motion.div>
          </AnimatePresence>
          <p className="absolute inset-x-5 bottom-5 text-center text-[12.5px] text-paper/55 sm:inset-x-6 sm:bottom-6" data-testid="ticket-story-caption">{step.caption}</p>
        </div>
        {/* Active beat text – mobile only (desktop reads it in the rail) */}
        {!desktop && <div className="mt-5" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={step.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3, ease: EASE }}>
              <h3 className="t-h3 text-paper">{step.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-paper/70">{step.text}</p>
            </motion.div>
          </AnimatePresence>
        </div>}
      </div>
    </div>
  );
};
